import { useCallback, useEffect, useMemo, useState } from 'react';

import { Form } from 'antd';
import { useDispatch } from 'dva';
import _ from 'lodash';
import { useIntl } from 'umi';

import { assignFieldValue } from '@/utils/infrastructure';

const extractFieldId = (raw) => raw?.value ?? raw?.id ?? raw;
const extractFieldLabel = (raw) => raw?.label ?? raw?.name ?? raw?.fullName ?? raw;

/** Full option from remote select cache — labelInValue alone drops position/description. */
const resolveSelectedOption = (rawValue, fieldName, global) => {
  const id = extractFieldId(rawValue);
  const options = global?.apiData?.[fieldName]?.data;
  if (!Array.isArray(options) || id == null || id === '') return rawValue;
  return options.find((opt) => opt.value === id || opt.id === id) ?? rawValue;
};

/**
 * Maps DVA state for a relation-list drawer (N:M membership editor).
 */
export function mapRelationListDrawerState(namespace, stateMap) {
  return ({ [namespace]: modelState, global, loading }) => ({
    visible: modelState[stateMap.visible],
    data: modelState[stateMap.data],
    config: modelState[stateMap.config],
    parentItem: modelState[stateMap.parentItem],
    global,
    loadingFetch: loading.effects[stateMap.fetchEffect],
    loadingSave: loading.effects[stateMap.saveEffect],
    deleteId: global.deleteId,
    modalConfirmDrawer: global.modalConfirmDrawer,
  });
}

/**
 * Maps DVA state for a multi-tab permission drawer (`RelationPermissionsDrawer`).
 * `tabs` entries: `{ key, data, config?, fetchEffect?, fetchingKey? }`.
 * Prefer `fetchingKey` when the model tracks per-list fetch via `fetchingPermissionList`
 * (shared effect name would mark all tabs loading).
 */
export function mapRelationPermissionsState(namespace, stateMap) {
  return ({ [namespace]: modelState, global, loading }) => {
    const fetching = modelState.fetchingPermissionList;
    const tabsState = (stateMap.tabs || []).map((tab) => ({
      key: tab.key,
      data: modelState[tab.data],
      config: tab.config ? modelState[tab.config] : undefined,
      loadingFetch: tab.fetchingKey
        ? fetching === tab.fetchingKey
        : Boolean(loading.effects[tab.fetchEffect]),
    }));

    return {
      visible: modelState[stateMap.visible],
      parentItem: modelState[stateMap.parentItem],
      global,
      loadingSave: loading.effects[stateMap.saveEffect],
      deleteId: global.deleteId,
      modalConfirmDrawer: global.modalConfirmDrawer,
      tabsState,
    };
  };
}

/**
 * Headless orchestration for drawers that manage a related-item list:
 * fetch on open, add via selector, bulk/single delete, dirty tracking, save.
 *
 * Standalone: one list + own save/close/hide (group-users).
 * Embedded tab (`embedded: true`): used inside RelationPermissionsDrawer —
 * fetch when `active`, keep dirty across tab switches, parent owns save/close/hide.
 *
 * Tradeoff (permissions drawer): all tabs mount their hooks while the drawer is open
 * so dirty/add state survives tab switches. Costlier on open; longer-term we could
 * mount only the active tab body if hooks can stay stable without always-on embeds.
 */
export function useRelationListDrawer({
  visible,
  active = true,
  embedded = false,
  parentId,
  parentItem,
  data,
  config,
  global,
  loadingFetch,
  loadingSave,
  deleteId,
  fieldName,
  duplicateErrorId,
  resetApiDataKeys = [],
  effects,
  buildFetchPayload,
  buildAddPayload,
  buildSavePayload,
  buildDeleteAction,
  hideDelayMs = 500,
}) {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [form] = Form.useForm();

  const [addOpen, setAddOpen] = useState(false);
  const [valid, setValid] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [formConfig, setFormConfig] = useState(config);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingFetch, setPendingFetch] = useState(false);
  const listData = Array.isArray(data) ? data : [];

  const loadList = useCallback(() => {
    if (!parentId || !effects?.fetch) return;
    dispatch({
      type: effects.fetch,
      payload: buildFetchPayload(parentId, parentItem),
    });
  }, [dispatch, effects?.fetch, buildFetchPayload, parentId, parentItem]);

  // Drawer open lifecycle (standalone) or first open (embedded parent visible).
  useEffect(() => {
    if (!visible) {
      setIsSaving(false);
      if (!embedded) setIsDirty(false);
      return;
    }

    if (!embedded) {
      setIsDirty(false);
    }

    if (config) {
      const fields = assignFieldValue(config, global, parentItem);
      setFormConfig({ ...config, fields });
    }
    // Scoped to drawer open — do not re-run on global/apiData changes (breaks remote select).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, parentId]);

  // Fetch when the tab becomes active (embedded) or drawer opens (standalone).
  useEffect(() => {
    if (!visible || !active || !parentId) return;
    setPendingFetch(true);
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, active, parentId]);

  // Clear pending when fetch finishes — or immediately if effect returned from cache
  // without flipping loading.effects (hand-rolled permission lists).
  useEffect(() => {
    if (!visible || !active) {
      setPendingFetch(false);
      return;
    }
    if (!pendingFetch) return;
    if (!loadingFetch) {
      setPendingFetch(false);
    }
  }, [visible, active, loadingFetch, pendingFetch]);

  // Standalone cleanup on close. Embedded tabs: parent owns hide/resetApiData.
  useEffect(() => {
    if (!visible || embedded) return undefined;

    return () => {
      setFormConfig(config);
      if (resetApiDataKeys.length) {
        dispatch({ type: 'global/resetApiData', payload: resetApiDataKeys });
      }
      if (effects?.hide) {
        setTimeout(() => {
          dispatch({ type: effects.hide });
        }, hideDelayMs);
      }
    };
    // Cleanup only when the drawer closes — not when apiData updates while open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // When leaving an embedded tab, close the add form / selection only.
  useEffect(() => {
    if (embedded && visible && !active) {
      setAddOpen(false);
      setValid(false);
      setSelectedRowKeys([]);
      setSelectedRows([]);
      setShowDeleteConfirm(false);
      form.resetFields();
    }
  }, [embedded, visible, active, form]);

  useEffect(() => {
    if (!isSaving || loadingSave) return;
    setIsSaving(false);
  }, [isSaving, loadingSave]);

  const cancelAdd = useCallback(() => {
    form.resetFields();
    setAddOpen(false);
    setValid(false);
    setIsSaving(false);
  }, [form]);

  const resetSelection = useCallback(() => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, []);

  const markDirty = useCallback(() => {
    setIsDirty(true);
  }, []);

  const clearDirty = useCallback(() => {
    setIsDirty(false);
  }, []);

  const onClose = useCallback(() => {
    resetSelection();
    setShowDeleteConfirm(false);
    setIsSaving(false);
    setIsDirty(false);
    setAddOpen(false);
    if (effects?.close) {
      dispatch({ type: effects.close });
    }
    cancelAdd();
    if (resetApiDataKeys.length) {
      dispatch({ type: 'global/resetApiData', payload: resetApiDataKeys });
    }
  }, [dispatch, effects?.close, cancelAdd, resetApiDataKeys, resetSelection]);

  const openAdd = useCallback(() => {
    if (config) {
      const fields = assignFieldValue(config, global, parentItem);
      setFormConfig({ ...config, fields });
    }
    setAddOpen(true);
    setValid(false);
  }, [config, global, parentItem]);

  const valueChangesHandler = useMemo(
    () =>
      _.debounce((_, values) => {
        const rawValue = values[fieldName];
        if (rawValue) {
          const id = extractFieldId(rawValue);
          const exists = listData.some((entry) => entry.id === id);
          setValid(!exists);
          form.setFields([
            {
              name: fieldName,
              errors: exists ? [formatMessage({ id: duplicateErrorId })] : [],
            },
          ]);
        } else {
          setValid(false);
          form.setFields([{ name: fieldName, errors: [] }]);
        }
      }, 500),
    [listData, fieldName, duplicateErrorId, form, formatMessage],
  );

  useEffect(() => () => valueChangesHandler.cancel(), [valueChangesHandler]);

  const finishHandler = useCallback(
    (payload) => {
      const rawValue = payload[fieldName];
      const id = extractFieldId(rawValue);
      const selected = resolveSelectedOption(rawValue, fieldName, global);
      const label = extractFieldLabel(selected) || extractFieldLabel(rawValue);

      if (listData.some((entry) => entry.id === id)) {
        form.setFields([
          {
            name: fieldName,
            errors: [formatMessage({ id: duplicateErrorId })],
          },
        ]);
        return;
      }

      dispatch({
        type: effects.add,
        payload: buildAddPayload(id, label, parentItem, selected),
      });
      setAddOpen(false);
      setIsDirty(true);
      form.resetFields();
      setValid(false);
    },
    [
      listData,
      dispatch,
      effects.add,
      fieldName,
      duplicateErrorId,
      form,
      formatMessage,
      parentItem,
      buildAddPayload,
      global,
    ],
  );

  const dispatchDelete = useCallback(
    (id) => {
      if (typeof buildDeleteAction === 'function') {
        dispatch(buildDeleteAction(id));
        return;
      }
      dispatch({ type: effects.delete, id });
    },
    [dispatch, effects?.delete, buildDeleteAction],
  );

  const deleteSelectedItems = useCallback(() => {
    selectedRowKeys.forEach((id) => {
      dispatchDelete(id);
    });
    resetSelection();
    setShowDeleteConfirm(false);
    setIsDirty(true);
  }, [dispatchDelete, resetSelection, selectedRowKeys]);

  const confirmDelete = useCallback(() => {
    dispatchDelete(deleteId);
    setIsDirty(true);
    dispatch({
      type: 'global/modalConfirmDrawer',
      payload: { state: false, id: null },
    });
  }, [dispatch, dispatchDelete, deleteId]);

  const deleteItemConfirm = useCallback(
    (id) => {
      dispatch({
        type: 'global/modalConfirmDrawer',
        payload: { state: true, id },
      });
    },
    [dispatch],
  );

  const save = useCallback(() => {
    if (!effects?.save || !buildSavePayload) return;
    setIsSaving(true);
    dispatch({
      type: effects.save,
      payload: buildSavePayload(listData, parentItem),
    });
  }, [dispatch, effects?.save, buildSavePayload, listData, parentItem]);

  const onSelectChange = useCallback((newSelectedRowKeys, newSelectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);
  }, []);

  const isTableLoading = Boolean(parentId && active && (loadingFetch || pendingFetch));
  const primaryDisabled = !isDirty || loadingSave;
  const primaryLoading = isSaving || loadingSave;

  return {
    form,
    formConfig,
    addOpen,
    valid,
    isDirty,
    isSaving,
    isTableLoading,
    primaryDisabled,
    primaryLoading,
    selectedRowKeys,
    selectedRows,
    showDeleteConfirm,
    setShowDeleteConfirm,
    openAdd,
    cancelAdd,
    onClose,
    valueChangesHandler,
    finishHandler,
    deleteSelectedItems,
    confirmDelete,
    deleteItemConfirm,
    save,
    onSelectChange,
    markDirty,
    clearDirty,
  };
}
